import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./ui/sheet";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "./ui/alert-dialog";
import { CATEGORIES, CATEGORY_MAP, PRIORITIES, RECURRENCES, STATUS_STYLES, formatDate } from "../lib/constants";
import { Check, Trash2, Pencil, Bell, Download, X, CalendarPlus } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";

export default function ItemDetailDrawer({ itemId, open, onClose, onChanged }) {
  const { t } = useTranslation();
  const [item, setItem] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});

  useEffect(() => {
    if (open && itemId) {
      setEditing(false);
      api.get(`/items/${itemId}`).then(({ data }) => { setItem(data); setForm(data); });
    }
  }, [open, itemId]);

  if (!item) return null;
  const cat = CATEGORY_MAP[item.category] || CATEGORY_MAP.other;
  const Icon = cat.icon;
  const cls = STATUS_STYLES[item.status] || STATUS_STYLES.upcoming;
  const dateLabel = t(`eventTypes.${item.event_type || "other"}`);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const refresh = async () => {
    const { data } = await api.get(`/items/${itemId}`);
    setItem(data); setForm(data);
    onChanged && onChanged();
  };

  const saveEdit = async () => {
    try {
      await api.patch(`/items/${itemId}`, {
        title: form.title, description: form.description, category: form.category,
        amount: form.amount === "" || form.amount == null ? null : Number(form.amount),
        currency: form.currency || null, due_date: form.due_date || null,
        action: form.action, priority: form.priority, recurrence: form.recurrence,
        reminder_offsets: form.reminder_offsets,
      });
      toast.success(t("item.updated"));
      setEditing(false);
      await refresh();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const complete = async () => {
    await api.post(`/items/${itemId}/complete`);
    toast.success(t("item.markedComplete"));
    onChanged && onChanged();
    onClose();
  };

  const remove = async () => {
    await api.delete(`/items/${itemId}`);
    toast.success(t("item.deleted"));
    onChanged && onChanged();
    onClose();
  };

  const downloadFile = async () => {
    try {
      const { data } = await api.get(`/files/${item.file_id}`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(t("item.openError"));
    }
  };

  const addToCalendar = async () => {
    try {
      const { data } = await api.get(`/items/${itemId}/calendar.ics`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "text/calendar" }));
      const a = document.createElement("a");
      a.href = url; a.download = `aniki-${itemId}.ics`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      toast.success(t("item.addedToCalendar"));
    } catch (e) {
      toast.error(t("item.calendarError"));
    }
  };

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="item-detail-drawer">
        <SheetHeader>
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-xl flex items-center justify-center" style={{ backgroundColor: cat.bg }}>
              <Icon className="h-6 w-6" style={{ color: cat.color }} />
            </div>
            <div>
              <SheetTitle className="font-display text-left">{editing ? t("item.editTitle") : item.title}</SheetTitle>
              <span className={`inline-block mt-1 text-[11px] px-2 py-0.5 rounded-full border font-medium ${cls}`}>
                {t(`status.${item.status}`)}
              </span>
            </div>
          </div>
        </SheetHeader>

        {!editing ? (
          <div className="mt-6 space-y-4 text-sm">
            {item.description && <p className="text-muted-foreground">{item.description}</p>}
            <Row label={t("item.action")} value={item.action || "—"} />
            <Row label={t("item.category")} value={t(`categories.${item.category}`)} />
            <Row label={dateLabel} value={formatDate(item.due_date)} />
            <Row label={t("item.amount")} value={item.amount != null ? `${item.currency || ""} ${item.amount}` : "—"} />
            <Row label={t("item.priority")} value={t(`priority.${item.priority}`)} />
            <Row label={t("item.recurrence")} value={t(`recurrence.${item.recurrence}`)} />
            <Row label={t("item.entity")} value={item.entity || "—"} />
            <Row label={t("item.created")} value={item.created_at ? formatDate(item.created_at.slice(0, 10)) : "—"} />

            {item.source_document && (
              <div className="flex items-center justify-between border-t border-border pt-3">
                <span className="text-muted-foreground text-xs">{t("item.sourceDocument")}</span>
                <Button data-testid="btn-download-source" variant="outline" size="sm" onClick={downloadFile}>
                  <Download className="h-3.5 w-3.5 mr-1" /> {item.source_document?.slice(0, 18)}
                </Button>
              </div>
            )}

            <div className="border-t border-border pt-3">
              <p className="text-xs text-muted-foreground mb-2 flex items-center gap-1"><Bell className="h-3.5 w-3.5" /> {t("item.reminders")}</p>
              {(item.reminders || []).length === 0 && <p className="text-xs text-muted-foreground">{t("item.noRemindersScheduled")}</p>}
              <div className="space-y-1">
                {(item.reminders || []).map((r) => (
                  <div key={r.id} className="text-xs flex justify-between bg-secondary rounded-md px-2 py-1">
                    <span>{formatDate(r.remind_on)}</span>
                    <span className="text-muted-foreground">{t("reminders.dBefore", { count: r.offset_days })} {r.sent ? `· ${t("item.sent")}` : ""}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex flex-wrap gap-2 pt-4">
              {item.status !== "completed" && (
                <Button data-testid="btn-item-complete" onClick={complete} className="brand-bg text-white hover:opacity-90">
                  <Check className="h-4 w-4 mr-1" /> {t("common.complete")}
                </Button>
              )}
              <Button data-testid="btn-item-edit" variant="outline" onClick={() => setEditing(true)}>
                <Pencil className="h-4 w-4 mr-1" /> {t("common.edit")}
              </Button>
              {item.due_date && (
                <Button data-testid="btn-add-to-calendar" variant="outline" onClick={addToCalendar}>
                  <CalendarPlus className="h-4 w-4 mr-1" /> {t("item.addToCalendar")}
                </Button>
              )}
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button data-testid="btn-item-delete" variant="outline" className="text-red-600 hover:text-red-700">
                    <Trash2 className="h-4 w-4 mr-1" /> {t("common.delete")}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>{t("item.deleteConfirmTitle")}</AlertDialogTitle>
                    <AlertDialogDescription>{t("item.deleteConfirmDesc")}</AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
                    <AlertDialogAction data-testid="btn-confirm-delete" onClick={remove} className="bg-red-600 hover:bg-red-700">{t("common.delete")}</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        ) : (
          <div className="mt-6 space-y-3">
            <div><Label className="text-xs">{t("review.fieldTitle")}</Label><Input data-testid="edit-title" value={form.title || ""} onChange={(e) => set("title", e.target.value)} /></div>
            <div><Label className="text-xs">{t("review.summary")}</Label><Textarea value={form.description || ""} rows={2} onChange={(e) => set("description", e.target.value)} /></div>
            <div><Label className="text-xs">{t("review.actionRequired")}</Label><Input value={form.action || ""} onChange={(e) => set("action", e.target.value)} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">{t("item.category")}</Label>
                <Select value={form.category} onValueChange={(v) => set("category", v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c.id} value={c.id}>{t(`categories.${c.id}`)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">{dateLabel}</Label>
                <Input data-testid="btn-item-reschedule" type="date" value={form.due_date || ""} onChange={(e) => set("due_date", e.target.value)} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">{t("review.amount")}</Label><Input type="number" value={form.amount ?? ""} onChange={(e) => set("amount", e.target.value)} /></div>
              <div><Label className="text-xs">{t("review.currency")}</Label><Input value={form.currency || ""} onChange={(e) => set("currency", e.target.value)} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">{t("item.priority")}</Label>
                <Select value={form.priority} onValueChange={(v) => set("priority", v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p} value={p}>{t(`priority.${p}`)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">{t("item.recurrence")}</Label>
                <Select value={form.recurrence} onValueChange={(v) => set("recurrence", v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{RECURRENCES.map((r) => <SelectItem key={r} value={r}>{t(`recurrence.${r}`)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex gap-2 pt-2">
              <Button data-testid="btn-save-edit" onClick={saveEdit} className="brand-bg text-white hover:opacity-90">{t("common.saveChanges")}</Button>
              <Button variant="ghost" onClick={() => { setEditing(false); setForm(item); }}><X className="h-4 w-4 mr-1" />{t("common.cancel")}</Button>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between items-center border-b border-border/60 pb-2">
      <span className="text-muted-foreground text-xs">{label}</span>
      <span className="font-medium">{value}</span>
    </div>
  );
}
